import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import { NAV_LINKS, CONTACT } from "@/data/content";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { cn } from "@/lib/utils";

export function scrollToHash(hash) {
    const el = document.querySelector(hash);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -64, duration: 1.4 });
    else el.scrollIntoView({ behavior: "smooth" });
}

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (e, hash) => {
        e.preventDefault();
        setOpen(false);
        if (location.pathname !== "/") navigate("/", { state: { scrollTo: hash } });
        else scrollToHash(hash);
    };

    const linkCls = "nav-link font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-white/75 transition-colors hover:text-white";

    return (
        <header
            data-testid="main-navbar"
            className={cn(
                "fixed inset-x-0 top-0 z-50 transition-all duration-500",
                scrolled || location.pathname !== "/"
                    ? "border-b border-white/10 bg-omsivo-deep/85 backdrop-blur-xl"
                    : "border-b border-transparent bg-gradient-to-b from-omsivo-deep/80 to-transparent"
            )}
        >
            <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-4 sm:px-6 lg:h-[72px] lg:px-10">
                <a href="#home" onClick={(e) => go(e, "#home")} className="flex items-center gap-3" data-testid="nav-home-logo">
                    <Logo className="h-10 w-10 lg:h-11 lg:w-11" />
                    <span className="font-display text-lg font-extrabold tracking-[0.18em] text-white">OMSIVO</span>
                </a>

                <nav className="hidden items-center gap-5 xl:gap-7 lg:flex" aria-label="Primary">
                    {NAV_LINKS.map((l) =>
                        l.to ? (
                            <Link key={l.label} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={cn(linkCls, location.pathname === l.to && "text-flame")}>
                                {l.label}
                            </Link>
                        ) : (
                            <a key={l.hash} href={l.hash} onClick={(e) => go(e, l.hash)}
                                data-testid={`nav-link-${l.label.toLowerCase()}`} className={linkCls}>
                                {l.label}
                            </a>
                        )
                    )}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href={CONTACT.phoneTel}
                        onClick={() => trackEvent(EVENTS.phoneClick, { location: "navbar" })}
                        data-testid="nav-phone-link"
                        className="hidden items-center gap-2 font-mono text-xs tracking-[0.14em] text-white/80 transition-colors hover:text-white xl:flex"
                    >
                        <Phone className="h-3.5 w-3.5 text-flame" />
                        {CONTACT.phoneDisplay}
                    </a>
                    <a
                        href="#contact"
                        onClick={(e) => { trackEvent(EVENTS.estimateClick, { location: "navbar" }); go(e, "#contact"); }}
                        data-testid="nav-get-estimate-btn"
                        className="hidden rounded-full bg-flame px-5 py-2.5 font-display text-[13px] font-bold uppercase tracking-[0.08em] text-white shadow-lg shadow-flame/25 transition-all hover:-translate-y-0.5 hover:bg-flame-dark sm:block"
                    >
                        Get Free Estimate
                    </a>
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                aria-label="Open menu"
                                data-testid="nav-mobile-menu-btn"
                                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white lg:hidden"
                            >
                                <Menu className="h-5 w-5" />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-[86vw] border-l border-white/10 bg-omsivo-deep p-0 sm:max-w-sm" data-testid="nav-mobile-drawer">
                            <SheetTitle className="sr-only">OMSIVO Navigation</SheetTitle>
                            <div className="flex h-full flex-col px-7 pb-8 pt-10" data-lenis-prevent>
                                <div className="flex items-center gap-3">
                                    <Logo className="h-11 w-11" />
                                    <span className="font-display text-lg font-extrabold tracking-[0.18em] text-white">OMSIVO</span>
                                </div>
                                <nav className="mt-10 flex flex-col gap-1 overflow-y-auto" aria-label="Mobile" data-lenis-prevent>
                                    {NAV_LINKS.map((l, i) => {
                                        const inner = (
                                            <>
                                                <span className="font-mono text-[10px] text-flame">{String(i + 1).padStart(2, "0")}</span>
                                                <span className="font-display text-xl font-bold tracking-wide text-white/90 transition-colors group-hover:text-flame">
                                                    {l.label}
                                                </span>
                                            </>
                                        );
                                        const cls = "group flex items-baseline gap-4 border-b border-white/8 py-3.5";
                                        return l.to ? (
                                            <Link key={l.label} to={l.to} onClick={() => setOpen(false)}
                                                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className={cls}>
                                                {inner}
                                            </Link>
                                        ) : (
                                            <a key={l.hash} href={l.hash} onClick={(e) => go(e, l.hash)}
                                                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`} className={cls}>
                                                {inner}
                                            </a>
                                        );
                                    })}
                                </nav>
                                <div className="mt-auto grid grid-cols-2 gap-3 pt-8">
                                    <a href={CONTACT.phoneTel} data-testid="nav-mobile-call-btn" onClick={() => trackEvent(EVENTS.phoneClick, { location: "drawer" })}
                                        className="flex items-center justify-center gap-2 rounded-full border border-white/20 py-3 font-display text-xs font-bold uppercase tracking-wider text-white">
                                        <Phone className="h-4 w-4" /> Call
                                    </a>
                                    <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-whatsapp-btn"
                                        onClick={() => trackEvent(EVENTS.whatsappClick, { location: "drawer" })}
                                        className="flex items-center justify-center gap-2 rounded-full border border-white/20 py-3 font-display text-xs font-bold uppercase tracking-wider text-white">
                                        WhatsApp
                                    </a>
                                    <a href="#contact" onClick={(e) => go(e, "#contact")} data-testid="nav-mobile-estimate-btn"
                                        className="col-span-2 rounded-full bg-flame py-3.5 text-center font-display text-sm font-bold uppercase tracking-wider text-white">
                                        Get Free Estimate
                                    </a>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};
